import * as React from 'react';
import {useSelector, useDispatch} from 'react-redux';

import {serializeFilters} from 'nutshell-core';

import {formEncodedApi} from 'nutshell-core/api';
import {Routing} from 'nutshell-core/routing';
import {
    replaceSavedFilter,
    requestResaveFilter,
    requestSaveNewFilter,
    updateResaveFilter,
    updateSavedFilters,
    updateSaveNewFilter,
    failResaveFilter,
    failSaveNewFilter,
} from 'nutshell-core/saved-filters/saved-filters-actions';
import {Stagesets} from 'nutshell-core/stagesets';
import type {CoreEntityType, Stageset} from 'nutshell-core/types';

import {SearchBar} from 'shells/navigation/search-bar/search-bar';
import {SAVED_LIST_TOUR_YOUR_LISTS_ID, SAVED_LIST_TOUR_SAVED_LISTS_ID} from 'shells/tours/tours';

import type {
    CreateSavedFilter_filterCreate_filter as SavedFilter,
    CreateFixedAudience_audienceCreate_audience as Audience,
} from 'nutshell-graphql-types';

import {appHistory} from '../../../history';
import {addNotification} from '../../../notifications/index';
import {removeListParamFromValue} from '../../../saved-filters/saved-filters-utils';

import {useGetSessionUser} from '../../core/use-get-session-user';

import {useGetSavedFilters, useGetDefaultFilters} from '../graphql/hooks';
import {SaveListModal} from '../save-list-modal/save-list-modal';

import {AudienceSectionWrapper} from './audience-section-wrapper';
import {
    getSavedFiltersReportType,
    getNavItemsForDraftSection,
    getNavItemsFromSavedFilters,
    buildSavedListObjectForRedux,
} from './helpers';
import {SidebarSection} from './sidebar-section';
import type {ReduxSavedFilters, NavItemObj} from './types';

import styles from './entity-list-sidebar.module.css';

type ModalState = {
    isOpen: boolean;
    navItem: NavItemObj | null | undefined;
};

export type Props = {
    entityType: CoreEntityType;
    searchParams: any;
    entities: any;
};

export function EntityListSidebar(props: Props) {
    const formattedCount = props.entities ? props.entities.countFormatted : null;
    const unfilteredFormattedCount = props.entities
        ? props.entities.unfilteredCountFormatted
        : null;

    const [searchNameQuery, setSearchNameQuery] = React.useState<string>('');
    const [modalState, setModalState] = React.useState<ModalState>({
        isOpen: false,
        navItem: null,
    });

    const {user} = useGetSessionUser();

    const {savedFilters: savedLists, isLoading: savedListsLoading} = useGetSavedFilters({
        type: getSavedFiltersReportType(props.entityType),
        includeShared: false,
        searchNameQuery: searchNameQuery,
    });

    const {savedFilters: sharedLists, isLoading: sharedListsLoading} = useGetSavedFilters({
        type: getSavedFiltersReportType(props.entityType),
        includeShared: true,
        ignoreUserFilters: true,
        searchNameQuery: searchNameQuery,
    });

    const {defaultFilters, isLoading: defaultFiltersLoading} = useGetDefaultFilters({
        type: getSavedFiltersReportType(props.entityType),
        searchNameQuery: searchNameQuery,
    });

    const dispatch = useDispatch();

    // @ts-expect-error
    const savedFilters: ReduxSavedFilters = useSelector((state) => state.savedFilters.items);

    const stagesets: Stageset[] = useSelector((state) => Stagesets.getAll(state));

    const draftSectionNavItems = getNavItemsForDraftSection(
        props.entityType,
        props.searchParams,
        unfilteredFormattedCount,
        formattedCount,
        savedFilters,
        stagesets
    );

    const activeSavedListFilter =
        savedFilters && props.searchParams.list ? savedFilters[props.searchParams.list] : null;

    const savedListNavItems = getNavItemsFromSavedFilters(
        savedLists,
        activeSavedListFilter,
        window.location.search,
        props.searchParams,
        formattedCount,
        false
    );

    const allSharedLists = [...sharedLists, ...defaultFilters];
    const sharedListNavItems = getNavItemsFromSavedFilters(
        allSharedLists,
        activeSavedListFilter,
        window.location.search,
        props.searchParams,
        formattedCount,
        true
    );

    const handleOpenModal = (navItem: NavItemObj) => {
        setModalState({
            isOpen: true,
            navItem,
        });
    };

    function createFilterRedux(filter: SavedFilter) {
        const list = [buildSavedListObjectForRedux(filter, props.entityType, filter.filterString)];
        try {
            dispatch(requestSaveNewFilter());
            // @ts-expect-error
            dispatch(updateSavedFilters(list));
            dispatch(updateSaveNewFilter());
            // update url so new list is selected
            const params = Routing.deparam(filter.filterString);
            const newParams = {...params, list: filter.id} as const;
            appHistory.replace({search: Routing.param(newParams)});
        } catch (e: any) {
            dispatch(failSaveNewFilter());
            addNotification('Something went wrong', {
                icon: 'frowny',
                body: 'We encountered a problem while saving the list.',
            });
            throw new Error(e);
        }
        setTimeout(() => {
            handleModalClose();
        }, 1000);
    }

    function updateFilterRedux(filter: SavedFilter) {
        // update redux so list doesn't show as 'dirty'
        const list = buildSavedListObjectForRedux(filter, props.entityType, filter.filterString);
        try {
            dispatch(requestResaveFilter());
            // @ts-expect-error
            dispatch(replaceSavedFilter(list));
            dispatch(updateResaveFilter());
        } catch (e: any) {
            dispatch(failResaveFilter());
            addNotification('Something went wrong', {
                icon: 'frowny',
                body: 'We encountered a problem while updating the list.',
            });
            throw new Error(e);
        }
        setTimeout(() => {
            handleModalClose();
        }, 1000);
    }

    function handleAudienceBulkEdit(
        audience: Audience,
        isModalForCurrentList: boolean,
        navItem: NavItemObj
    ) {
        let filter = serializeFilters(props.entities.query.filter.selected);
        let q = props.searchParams.q;
        if (!isModalForCurrentList) {
            if (navItem.type === 'audience') {
                filter = serializeFilters([
                    {
                        audiences: {
                            data: [{data: navItem.id}],
                        },
                    },
                ]);
            } else if (navItem.type === 'list' && navItem.link) {
                const deparamedValue = Routing.deparam(navItem.link);
                filter = serializeFilters(
                    Object.entries(deparamedValue.filter).map(([key, value]: [any, any]) => ({
                        [key]: value,
                    }))
                );
                q = deparamedValue.q;
            }
        }

        const postData = {
            audienceId: audience.id,
            newFilters: true,
            onlyAddPrimaryContactToAudience: true,
            filter,
            q,
            omittedItems: props.entities.omittedItems,
        } as const;

        const endpoint = '/bulk-edit-person/apply.json';

        return (
            formEncodedApi
                .post(endpoint, postData)
                // @ts-expect-error
                .then((response) => response.json())
                .then((responseJson) => {
                    if (
                        responseJson &&
                        responseJson.isValidationResult &&
                        !responseJson.isValid &&
                        responseJson.messages
                    ) {
                        throw new Error(responseJson.messages[0]);
                    }
                })
                .catch((e) => {
                    addNotification('Something went wrong', {
                        icon: 'frowny',
                        body: 'Could not add all people to audience.',
                    });
                    throw new Error(e);
                })
        );
    }

    const handleModalClose = () => {
        setModalState({
            isOpen: false,
            navItem: null,
        });
    };

    return (
        <>
            <div id={SAVED_LIST_TOUR_SAVED_LISTS_ID} className={styles.container}>
                <div className={styles['header-section']}>
                    <SidebarSection
                        entityType={props.entityType}
                        type='draft'
                        navItems={draftSectionNavItems}
                        onOpenModal={handleOpenModal}
                        listsAreImmutable={true}
                        searchNameQuery={searchNameQuery}
                        searchParams={props.searchParams}
                        isLoading={props.entities ? props.entities.firstLoad : true}
                    />
                </div>
                <div className={styles['search-bar-container']}>
                    <SearchBar
                        placeholder='Search for a list'
                        value={searchNameQuery}
                        isDark={true}
                        hasBorder={true}
                        onChange={(val) => setSearchNameQuery(val)}
                        isClearable={true}
                    />
                </div>
                <div className={styles['sidebar-sections']}>
                    <div id={SAVED_LIST_TOUR_YOUR_LISTS_ID}>
                        <SidebarSection
                            entityType={props.entityType}
                            header={
                                <span
                                    className='flex align-center gap-4 width-fit-content'
                                    data-tip='Lists that you have created'
                                >
                                    Your lists
                                </span>
                            }
                            type='saved'
                            navItems={savedListNavItems}
                            onOpenModal={handleOpenModal}
                            listsAreImmutable={false}
                            isLoading={savedListsLoading}
                            searchNameQuery={searchNameQuery}
                            searchParams={props.searchParams}
                            savedFilters={savedFilters || []}
                        />
                    </div>
                    <SidebarSection
                        entityType={props.entityType}
                        header={
                            <span
                                className='flex align-center gap-4 width-fit-content'
                                data-tip='Lists that others <br/>have shared with you'
                            >
                                Lists shared with you
                            </span>
                        }
                        type='shared'
                        navItems={sharedListNavItems}
                        onOpenModal={handleOpenModal}
                        listsAreImmutable={true}
                        isLoading={sharedListsLoading || defaultFiltersLoading}
                        searchNameQuery={searchNameQuery}
                        searchParams={props.searchParams}
                    />
                    {props.entityType === 'contacts' &&
                        user &&
                        // Show an upsell if they have no marketing access but hide if the user is a limited campaigns user
                        (!user.permissions.canAccessMarketing ||
                            user.permissions.canAccessFullCampaigns) && (
                            <>
                                <AudienceSectionWrapper
                                    searchParams={props.searchParams}
                                    onOpenModal={handleOpenModal}
                                    listsAreImmutable={false}
                                    searchNameQuery={searchNameQuery}
                                    selectedCount={formattedCount}
                                    shouldShowUpgrade={!user.permissions.canAccessMarketing}
                                />
                            </>
                        )}
                </div>
            </div>
            {modalState.navItem && user && (
                <SaveListModal
                    isOpen={modalState.isOpen}
                    onClose={handleModalClose}
                    currentFilterString={removeListParamFromValue(window.location.search)}
                    navItem={modalState.navItem}
                    entityType={props.entityType}
                    currentSelectedId={props.searchParams.list || props.searchParams.audience}
                    searchNameQuery={searchNameQuery}
                    createFilterRedux={createFilterRedux}
                    updateFilterRedux={updateFilterRedux}
                    handleAudienceBulkEdit={handleAudienceBulkEdit}
                    user={user}
                />
            )}
        </>
    );
}
